import { Radio, Stack } from '@qred/components-library';
import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { ValidationContext } from '~/components/hoc/withValidation';
import InputField from '~/components/shared/Input/InputField';
import { ECardPurpose } from '~/enums';
import useTranslate from '~/hooks/useTranslate';
import { RootState } from '~/store';
import { trackCardApplicationActions } from '~/store/actions/analyticsActions';
import { updateForm } from '~/store/slices/cardApplication.slice';
import { onChangeInputEvent } from '~/types/types';

export const CardPurpose = () => {
  const t = useTranslate();
  const dispatch = useAppDispatch();

  const validationContext = useContext(ValidationContext);

  const form = useSelector((state: RootState) => state.cardApplication.form);

  const otherPurposeReasonTextMaxCharLimit = 190;

  const options = Object.entries(cardPurposeTranslations).map(
    ([key, value]) => ({
      label: t(value),
      value: key,
    })
  );

  const onChangePurposeHandler = (value: string) => {
    dispatch(
      updateForm({
        purposeOfCard: value,
        cardPurposeManual: undefined,
      })
    );

    dispatch(
      trackCardApplicationActions({
        actionName: 'card_purpose_change',
      })
    );
  };

  const onChangeOtherPurposeHandler = (event: onChangeInputEvent) => {
    dispatch(updateForm({ cardPurposeManual: event.target.value }));
  };

  const onBlurOtherPurposeHandler = () => {
    dispatch(
      trackCardApplicationActions({
        actionName: 'card_purpose_other_change',
      })
    );
  };

  useEffect(() => {
    if (!form.purposeOfCard) {
      validationContext.addPropertyToValidationErrors('CardPurpose');
    }

    return () => {
      validationContext.removePropertyFromValidationErrors('CardPurpose');
    };
  }, [form.purposeOfCard]);

  return (
    <Stack spacing={'md'}>
      <Radio.Group
        name="purposes of card"
        onChange={onChangePurposeHandler}
        value={form.purposeOfCard}
        direction="column"
      >
        <Stack spacing={'lg'}>
          {options.map((option) => (
            <Radio
              key={option.value}
              value={option.value}
              label={option.label}
            />
          ))}
        </Stack>
      </Radio.Group>

      {form.purposeOfCard === ECardPurpose.OtherPurpose && (
        <InputField
          name="SpecifyCardPurpose"
          onChange={onChangeOtherPurposeHandler}
          onBlur={onBlurOtherPurposeHandler}
          validationType="ApplicationReasonOther"
          placeholder={
            t('CardApplication.CardPurposeOtherPurposePlaceHolder') as string
          }
          maxLength={otherPurposeReasonTextMaxCharLimit}
          value={form.cardPurposeManual}
          characterCounter
          autoFocus
        />
      )}
    </Stack>
  );
};

export const cardPurposeTranslations: Record<ECardPurpose, string> = {
  [ECardPurpose.Inventory]: 'CardApplication.CardPurposeInventory',
  [ECardPurpose.ToolsAndEquipment]:
    'CardApplication.CardPurposeToolsAndEquipment',
  [ECardPurpose.ComputerSoftware]:
    'CardApplication.CardPurposeComputerSoftware',
  [ECardPurpose.OfficeExpenses]: 'CardApplication.CardPurposeOfficeExpenses',
  [ECardPurpose.SalariesConsultancy]:
    'CardApplication.CardPurposeSalariesAndConsultancy',
  [ECardPurpose.Marketing]: 'CardApplication.CardPurposeMarketing',
  [ECardPurpose.Travel]: 'CardApplication.CardPurposeTraveling',
  [ECardPurpose.MealsAndDrinks]: 'CardApplication.CardPurposeMealsAndDrinks',
  [ECardPurpose.OtherPurpose]: 'CardApplication.CardPurposeOtherPurpose',
};
